import { Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  collectionData,
  deleteDoc,
  deleteField,
  doc,
  docData,
  CollectionReference,
  DocumentData,
  Firestore,
  getAggregateFromServer,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  sum,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  runTransaction,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import { path_operational_stores_data_storeId_messages_from_app } from '../../../shared/shared-services/database-paths';
import { createOperationalServerTimestamp } from '../../../shared/shared-utils/firestore/operational-server-timestamp.utils';
import { IFirebaseDataTimeStamps } from '../../core/models/data-time-stamps';
import { toRawStockCode, toStockDocId } from '../../../shared/shared-utils/firestore/firestore.utils';
import { createSharedPaths } from '../../../shared/shared-services/database-paths.shared';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { CoreService } from '../../core/service/core.service';
import {
  IStockItemApiData,
  ITopValueItem,
  IUncountedBand,
  IStockTake,
  IStockTakeCreate,
  IStockTakeFinalFigure,
  IStockTakeItem,
  IStockTakeItemStored,
  IStockTakePendingBarcodeUpdate,
  IStoreWideStockDashboardMetrics,
  StockTakeItemStatus,
  StockTakeStatus,
  STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS,
} from '../models';
import { IStockTakeItemCapacity } from '../utils/stock-take-limits.utils';
import { IStockTakeFromAppMessageData } from '../models/stock-take-backend-processing.interface';
import { StockTakeItemsChunkService } from './stock-take-items-chunk.service';
import { StockTakeFinalisedChunkService } from './stock-take-finalised-chunk.service';
import {
  STOCK_TAKE_FINALISED_STORAGE_SCHEMA_CURRENT,
  STOCK_TAKE_MESSAGE_TYPE,
} from '../utils/stock-take-finalised-storage.constants';
import {
  STOCK_FIELD_STOCK_TAKE_DATE,
  STOCK_FIELD_STOCK_VALUE,
  STOCK_TAKE_DASHBOARD_COVERAGE_DAYS,
  STOCK_TAKE_DASHBOARD_LOW_STOCK_VALUE_ZAR,
  STOCK_TAKE_DASHBOARD_TOP_ITEMS_LIMIT,
  STOCK_TAKE_DASHBOARD_UNCOUNTED_BAND_DEFS,
  STOCK_TAKE_NEVER_COUNTED_CUTOFF_MS,
  filterStockTakeHighRiskTopValueItems,
  parseStockTakeDateToIso,
  parseStockTakeDateToMillis,
  resolveStockTakeDashboardValidDateFloorMs,
  resolveStockTakeDaysSinceCounted,
} from '../utils/stock-take-stock-date.utils';
import { hasStockTakeReportItemCode } from '../utils/stock-take-item-eligibility.utils';
import {
  applyReportToItemToMetricAdjustment,
  createEmptyReportToItemMetricAdjustment,
  IReportToItemMetricAdjustment,
  subtractReportToItemsFromUncountedBands,
} from '../utils/stock-take-report-item-metrics.utils';
import { sItemKeyToInt } from '../../../shared-utilities/models-old/datastructures';

/**
 * Firestore path helpers for the stock take feature.
 *
 * Structure:
 *   operational/stores_data/{storeId}/data/stock_takes/stock_takes/stock_takes/{stockTakeId}
 *     ├── items_meta/_meta + item_chunks/{chunkId}           (live lines)
 *     └── finalised_meta/_meta + finalised_chunks/{chunkId}  (submitted figures)
 */
const stockTakesPath = (storeId: string): string =>
  `operational/stores_data/${storeId}/data/stock_takes/stock_takes/stock_takes`;

const stockTakePath = (storeId: string, stockTakeId: string): string =>
  `${stockTakesPath(storeId)}/${stockTakeId}`;

/**
 * Result of a paged completed-stock-takes read — items plus the last
 * document snapshot to use as the cursor for the next page.
 */
export interface IStockTakesPageResult {
  hasMore: boolean;
  items: IStockTake[];
  lastDoc: QueryDocumentSnapshot | null;
}

/**
 * Counts per status for tab badges, plus {@link all} as the sum of those
 * statuses (SUCCESS is omitted — it can be huge and is not shown on the Completed tab).
 * Status keys mirror the StockTakeStatus enum string values.
 */
export type IStockTakeStatusCounts = Record<string, number> & {
  all: number;
};

/**
 * Firebase service for stock take CRUD and finalisation operations
 */
@Injectable({
  providedIn: 'root',
})
export class StockTakeFirebaseService extends CoreService {
  /**
   * Cursor snapshots for the paged completed query, keyed by store. Kept
   * here (not in NgRx) because Firestore snapshots are not serialisable.
   */
  private readonly completedCursors = new Map<string, QueryDocumentSnapshot | null>();

  /** Cached per store — avoids repeated failed `_stock_take_date` inequality queries. */
  private readonly stockTakeDateQueryableByStoreId = new Map<string, boolean>();

  /** Cached per store — value sum with a coverage date filter may need a composite index. */
  private readonly stockTakeCoverageValueAggregateQueryableByStoreId = new Map<string, boolean>();

  constructor(
    firestore: Firestore,
    private readonly itemsChunkService: StockTakeItemsChunkService,
    private readonly finalisedChunkService: StockTakeFinalisedChunkService,
  ) {
    super(firestore);
  }

  /**
   * Get a single stock take document by ID (one-shot).
   */
  async getStockTakeOnce(storeId: string, stockTakeId: string): Promise<IStockTake | null> {
    const snap = await getDoc(doc(this.firestore, stockTakePath(storeId, stockTakeId)));
    if (!snap.exists()) {
      return null;
    }
    return { id: snap.id, ...(snap.data() as Omit<IStockTake, 'id'>) };
  }

  /**
   * Get a single stock take document by ID (real-time)
   */
  getStockTake(storeId: string, stockTakeId: string): Observable<IStockTake | undefined> {
    const path = stockTakePath(storeId, stockTakeId);
    const docRef = doc(this.firestore, path);
    return docData(docRef, { idField: 'id' }).pipe(
      map((data: DocumentData | undefined) => data as IStockTake | undefined),
    );
  }

  /**
   * Get all stock takes for a store, optionally filtered by status or several statuses (real-time).
   * Multiple statuses use a Firestore `in` query (same path as the Failed tab).
   */
  getStockTakes(storeId: string, status?: StockTakeStatus | StockTakeStatus[]): Observable<IStockTake[]> {
    const colRef = collection(this.firestore, stockTakesPath(storeId));
    let q;
    if (status === undefined) {
      q = query(colRef);
    } else if (Array.isArray(status)) {
      q = status.length > 0 ? query(colRef, where('status', 'in', status)) : query(colRef);
    } else {
      q = query(colRef, where('status', '==', status));
    }

    return collectionData(q, { idField: 'id' }).pipe(
      map((docs: DocumentData[]) => docs as IStockTake[]),
    );
  }

  /**
   * Aggregate count per status for a store (excluding SUCCESS). Uses
   * Firestore's count() aggregation so no documents are read — only the
   * totals. {@link IStockTakeStatusCounts.all} is the sum of the counted
   * statuses (not including completed stock takes).
   */
  async getStockTakeStatusCounts(storeId: string): Promise<IStockTakeStatusCounts> {
    const colRef = collection(this.firestore, stockTakesPath(storeId));
    const statuses: StockTakeStatus[] = [
      StockTakeStatus.inProgress,
      StockTakeStatus.prepared,
      StockTakeStatus.submitted,
      StockTakeStatus.failed,
    ];

    const perStatusSnaps = await Promise.all(
      statuses.map((s: StockTakeStatus) =>
        getCountFromServer(query(colRef, where('status', '==', s))),
      ),
    );

    const legacyPendingSnap = await getCountFromServer(
      query(colRef, where('status', '==', STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS)),
    );

    const counts: IStockTakeStatusCounts = { all: 0 };
    statuses.forEach((s: StockTakeStatus, i: number): void => {
      const n = perStatusSnaps[i].data().count;
      counts[s] = n;
      counts.all += n;
    });
    const legacyPending = legacyPendingSnap.data().count;
    counts[STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS] = legacyPending;
    counts.all += legacyPending;
    return counts;
  }

  /**
   * Count open stock takes whose startTime is older than the stale cutoff, and
   * prepared stock takes whose preparedTime (fallback: startTime) is older than a
   * shorter cutoff.
   * `openAndPreparedCount` is the stale-open total plus prepared takes that are
   * older than one day but not already inside that stale-open total.
   */
  async getOpenStockTakeDashboardCounts(
    storeId: string,
    staleOpenDays: number,
    preparedOlderThanDays: number,
  ): Promise<{ staleOpenCount: number; preparedOlderThanCount: number; openAndPreparedCount: number }> {
    const openStatuses: string[] = [
      StockTakeStatus.inProgress,
      StockTakeStatus.prepared,
      STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS,
    ];
    const openTakes = await firstValueFrom(
      this.getStockTakes(storeId, openStatuses as StockTakeStatus[]),
    );
    const nowMs = Date.now();
    const staleCutoffMs = nowMs - staleOpenDays * 86_400_000;
    const preparedCutoffMs = nowMs - preparedOlderThanDays * 86_400_000;
    let staleOpenCount = 0;
    let preparedOlderThanCount = 0;
    let openAndPreparedCount = 0;
    for (const take of openTakes) {
      const startedMs = take.startTime?.toMillis?.() ?? null;
      if (startedMs == null) {
        continue;
      }
      const isStaleOpen = startedMs < staleCutoffMs;
      const preparedAtMs = take.preparedTime?.toMillis?.() ?? startedMs;
      const isPreparedOlderThanCutoff =
        take.status === StockTakeStatus.prepared && preparedAtMs < preparedCutoffMs;
      if (isStaleOpen) {
        staleOpenCount += 1;
      }
      if (isPreparedOlderThanCutoff) {
        preparedOlderThanCount += 1;
      }
      if (isStaleOpen || isPreparedOlderThanCutoff) {
        openAndPreparedCount += 1;
      }
    }
    return { staleOpenCount, preparedOlderThanCount, openAndPreparedCount };
  }

  /**
   * Read a page of completed stock takes (status = SUCCESS), ordered by
   * {@link IStockTake.endTime} descending. Documents without `endTime` are
   * omitted from this query (Firestore `orderBy` behaviour).
   * Pagination is cursor-based.
   *
   * Pass `reset: true` to start a fresh paging session (e.g. when the user
   * switches to the completed tab). Subsequent calls without `reset` will
   * continue from the previous cursor.
   */
  async getCompletedStockTakesPage(
    storeId: string,
    pageSize: number,
    reset: boolean,
  ): Promise<IStockTakesPageResult> {
    if (reset) {
      this.completedCursors.delete(storeId);
    }

    const colRef = collection(this.firestore, stockTakesPath(storeId));
    const cursor = this.completedCursors.get(storeId);
    const q = cursor
      ? query(
          colRef,
          where('status', '==', StockTakeStatus.success),
          orderBy('endTime', 'desc'),
          startAfter(cursor),
          limit(pageSize),
        )
      : query(
          colRef,
          where('status', '==', StockTakeStatus.success),
          orderBy('endTime', 'desc'),
          limit(pageSize),
        );

    const snap = await getDocs(q);
    const items: IStockTake[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as IStockTake) }));
    const lastDoc = snap.docs[snap.docs.length - 1] ?? null;
    if (lastDoc) {
      this.completedCursors.set(storeId, lastDoc);
    }
    return {
      items,
      lastDoc,
      hasMore: snap.docs.length === pageSize,
    };
  }

  /**
   * Watch both transactions and delcolissuestran timestamps from the shared data_timestamps document.
   * Returns a real-time observable that emits whenever either timestamp changes in Firestore.
   */
  watchTransactionsTimestamps(storeId: string): Observable<{ transactions: Date | null; delColIssuesTran: Date | null; stock: Date | null }> {
    const path = createSharedPaths().setStoreId(storeId).dataTimestamps();
    return docData(doc(this.firestore, path)).pipe(
      map((data: DocumentData | undefined) => {
        const d = data as IFirebaseDataTimeStamps | undefined;
        const transactions = d?.transactions
          ? new Timestamp(d.transactions.seconds, d.transactions.nanoseconds).toDate()
          : null;
        const delColIssuesTran = d?.delcolissuestran
          ? new Timestamp(d.delcolissuestran.seconds, d.delcolissuestran.nanoseconds).toDate()
          : null;
        const stock = d?.stock
          ? new Timestamp(d.stock.seconds, d.stock.nanoseconds).toDate()
          : null;
        return { transactions, delColIssuesTran, stock };
      }),
    );
  }

  /**
   * Set a stock take's status to PREPARED and record when it became prepared.
   */
  async markStockTakeAsPrepared(storeId: string, stockTakeId: string): Promise<void> {
    const docRef = doc(this.firestore, stockTakePath(storeId, stockTakeId));
    await updateDoc(docRef, {
      status: StockTakeStatus.prepared,
      preparedTime: Timestamp.now(),
    });
  }

  /**
   * Set a stock take's status to IN_PROGRESS and clear preparedTime.
   */
  async markStockTakeAsInProgress(storeId: string, stockTakeId: string): Promise<void> {
    const docRef = doc(this.firestore, stockTakePath(storeId, stockTakeId));
    await updateDoc(docRef, {
      status: StockTakeStatus.inProgress,
      preparedTime: deleteField(),
    });
  }

  /**
   * When a prepared stock take has a pending line counted or removed, include
   * an in-progress status on the parent update. Returns whether the status will change.
   */
  private async includePreparedRevert(
    parentRef: ReturnType<typeof doc>,
    parentUpdate: Record<string, unknown>,
    shouldRevert: boolean,
  ): Promise<boolean> {
    if (!shouldRevert) {
      return false;
    }
    const snap = await getDoc(parentRef);
    if (snap.data()?.['status'] !== StockTakeStatus.prepared) {
      return false;
    }
    parentUpdate['status'] = StockTakeStatus.inProgress;
    parentUpdate['preparedTime'] = deleteField();
    return true;
  }

  /**
   * Set a stock take item's status to RECOUNT.
   * Decrements countedCount and increments recountCount when the line was COUNTED or UNIQUE.
   * If resetParentToInProgress is true, atomically resets the parent stock take status to IN_PROGRESS as well.
   */
  async markItemForRecount(
    storeId: string,
    stockTakeId: string,
    itemId: string,
    resetParentToInProgress: boolean = false,
  ): Promise<void> {
    const takePath = stockTakePath(storeId, stockTakeId);
    const storedItems = await this.itemsChunkService.loadStockTakeItems(takePath);
    const stored = storedItems.find((line) => line.id === itemId || line.code === itemId);
    if (!stored) {
      throw new Error(`Stock take item "${itemId}" could not be found`);
    }

    await this.itemsChunkService.updateItemByCode(takePath, itemId, {
      status: StockTakeItemStatus.recount,
    });

    const parentRef = doc(this.firestore, takePath);
    const parentUpdate: Record<string, unknown> = {};
    if (
      stored.status === StockTakeItemStatus.counted ||
      stored.status === StockTakeItemStatus.unique
    ) {
      parentUpdate['countedCount'] = increment(-1);
      parentUpdate['recountCount'] = increment(1);
    }
    if (resetParentToInProgress) {
      parentUpdate['status'] = StockTakeStatus.inProgress;
      parentUpdate['preparedTime'] = deleteField();
    }
    if (Object.keys(parentUpdate).length > 0) {
      await updateDoc(parentRef, parentUpdate);
    }
  }

  /**
   * Create a new stock take document
   */
  async createStockTake(storeId: string, data: IStockTakeCreate): Promise<string> {
    const colRef = collection(this.firestore, stockTakesPath(storeId));
    const payload: Record<string, unknown> = {
      name: data.name,
      userAssigned: data.userAssigned,
      status: StockTakeStatus.inProgress,
      startTime: Timestamp.now(),
      includeDelCol: data.includeDelCol !== false,
    };
    if (data.binLocation) {
      payload['binLocation'] = data.binLocation;
    }
    if (data.scheduleId) {
      payload['scheduleId'] = data.scheduleId;
    }
    const docRef = await addDoc(colRef, payload);
    return docRef.id;
  }

  /**
   * Update the bin location on an existing stock take document.
   */
  async updateStockTakeBinLocation(storeId: string, stockTakeId: string, binLocation: string): Promise<void> {
    const docRef = doc(this.firestore, stockTakePath(storeId, stockTakeId));
    await updateDoc(docRef, { binLocation });
  }

  /**
   * Batch-update the binL field (key '53') in the stock collection for all given codes.
   * Called when a stock take with a bin location is processed.
   */
  async updateBinLocationsInStock(storeId: string, codes: string[], binLocation: string): Promise<void> {
    if (codes.length === 0 || !binLocation) return;
    const stockPath = createSharedPaths().setStoreId(storeId).stock();
    const CHUNK_SIZE = 400;
    for (let i = 0; i < codes.length; i += CHUNK_SIZE) {
      const chunk = codes.slice(i, i + CHUNK_SIZE);
      const batch = writeBatch(this.firestore);
      for (const code of chunk) {
        const docRef = doc(this.firestore, `${stockPath}/${toStockDocId(code)}`);
        // eslint-disable-next-line @typescript-eslint/naming-convention
        batch.update(docRef, { '53': binLocation });
      }
      await batch.commit();
    }
  }

  /**
   * Update fields on a stock take document
   */
  async updateStockTake(
    storeId: string,
    stockTakeId: string,
    updates: Partial<Omit<IStockTake, 'id'>>,
  ): Promise<void> {
    const docRef = doc(this.firestore, stockTakePath(storeId, stockTakeId));
    await updateDoc(docRef, { ...updates });
  }

  /**
   * Delete a stock take document and all chunked subcollections.
   */
  async deleteStockTake(storeId: string, stockTakeId: string): Promise<void> {
    const takePath = stockTakePath(storeId, stockTakeId);
    await this.itemsChunkService.deleteAllChunks(takePath);
    await this.finalisedChunkService.deleteAllFinalisedChunks(takePath);
    await deleteDoc(doc(this.firestore, takePath));
  }

  /**
   * Batch-write new items into chunked item storage.
   * Also increments itemCount (and countedCount for COUNTED/UNIQUE items) on the parent document.
   */
  async batchWriteStockTakeItems(
    storeId: string,
    stockTakeId: string,
    items: Omit<IStockTakeItem, 'id'>[],
  ): Promise<void> {
    const takePath = stockTakePath(storeId, stockTakeId);
    await this.itemsChunkService.batchWriteItems(takePath, items);
    const countedItems = items.filter(
      (i) => i.status === StockTakeItemStatus.counted || i.status === StockTakeItemStatus.unique,
    ).length;
    const parentRef = doc(this.firestore, takePath);
    const parentUpdate: Record<string, unknown> = { itemCount: increment(items.length) };
    if (countedItems > 0) {
      parentUpdate['countedCount'] = increment(countedItems);
    }
    await updateDoc(parentRef, parentUpdate);
  }

  /**
   * Stage a barcode link on a stock take line (applied to stock on process).
   * Creates a PENDING line when the code is not already on the stock take.
   */
  async upsertPendingBarcodeAssignment(
    storeId: string,
    stockTakeId: string,
    code: string,
    pending: IStockTakePendingBarcodeUpdate,
    existingItemId?: string,
  ): Promise<void> {
    if (existingItemId) {
      await this.itemsChunkService.updateItemByCode(
        stockTakePath(storeId, stockTakeId),
        existingItemId,
        { pendingBarcodeUpdate: pending },
      );
      return;
    }
    await this.batchWriteStockTakeItems(storeId, stockTakeId, [
      {
        code,
        qtyCounted: 0,
        previousQtyCounted: 0,
        timesCounted: 0,
        status: StockTakeItemStatus.pending,
        pendingBarcodeUpdate: pending,
      },
    ]);
  }

  /**
   * Read primary and additional barcodes from the shared stock collection for updates on process.
   */
  async getStockBarcodeFieldsByCode(
    storeId: string,
    code: string,
  ): Promise<{ barcode: string; multipleBarcodes: string[]; desc: string }> {
    const stockPath = createSharedPaths().setStoreId(storeId).stock();
    const snap = await getDoc(doc(this.firestore, `${stockPath}/${toStockDocId(code)}`));
    if (!snap.exists()) {
      return { barcode: '', multipleBarcodes: [], desc: '' };
    }
    const raw = snap.data();
    const barcode = (raw['52'] as string) ?? '';
    const desc = (raw['1'] as string) ?? '';
    const multiRaw = raw['multipleBarcodes'] ?? raw['mb'] ?? [];
    const multipleBarcodes = Array.isArray(multiRaw)
      ? multiRaw.map((b: unknown) => String(b))
      : [];
    return { barcode, multipleBarcodes, desc };
  }

  /**
   * Batch-update existing stock take item lines (e.g. PENDING → COUNTED after scanning).
   * Also increments countedCount on the parent document for each item marked as COUNTED.
   */
  async batchUpdateStockTakeItems(
    storeId: string,
    stockTakeId: string,
    updates: {
      id: string;
      qtyCounted: number;
      previousQtyCounted: number;
      timesCounted: number;
      status: StockTakeItemStatus;
      countedByEmail?: string;
      firstCountedAt?: Timestamp;
    }[]): Promise<boolean> {
    const takePath = stockTakePath(storeId, stockTakeId);
    await this.itemsChunkService.batchUpdateItems(
      takePath,
      updates.map((update) => ({
        code: update.id,
        patch: {
          qtyCounted: update.qtyCounted,
          previousQtyCounted: update.previousQtyCounted,
          timesCounted: update.timesCounted,
          status: update.status,
          ...(update.countedByEmail ? { countedByEmail: update.countedByEmail } : {}),
          ...(update.firstCountedAt ? { firstCountedAt: update.firstCountedAt } : {}),
        },
      })),
    );
    const countedUpdates = updates.filter((u) => u.status === StockTakeItemStatus.counted).length;
    if (countedUpdates === 0) {
      return false;
    }
    const parentRef = doc(this.firestore, takePath);
    const parentUpdate: Record<string, unknown> = { countedCount: increment(countedUpdates) };
    const reverted = await this.includePreparedRevert(parentRef, parentUpdate, true);
    await updateDoc(parentRef, parentUpdate);
    return reverted;
  }

  /**
   * Fetch product details for a list of stock codes from Firestore shared stock collection.
   * Reads individual documents by their encoded code ID, in batches of 100.
   * Returns both the found data map and an array of codes that had no matching Firestore document
   * (i.e. removed from the stock file or a sync issue).
   */
  async getStockItemsByCode(
    storeId: string,
    codes: string[],
  ): Promise<{ data: Record<string, IStockItemApiData>; missingCodes: string[] }> {
    if (codes.length === 0) return { data: {}, missingCodes: [] };
    const stockPath = createSharedPaths().setStoreId(storeId).stock();
    const data: Record<string, IStockItemApiData> = {};
    const missingCodes: string[] = [];
    const CHUNK_SIZE = 100;
    const chunks: string[][] = [];
    for (let i = 0; i < codes.length; i += CHUNK_SIZE) {
      chunks.push(codes.slice(i, i + CHUNK_SIZE));
    }
    await Promise.all(
      chunks.map(async (chunk) => {
        const docRefs = chunk.map((code) =>
          doc(this.firestore, `${stockPath}/${toStockDocId(code)}`),
        );
        const snapshots = await Promise.all(docRefs.map((ref) => getDoc(ref)));
        snapshots.forEach((snap, idx) => {
          if (snap.exists()) {
            const raw = snap.data();
            const code = chunk[idx];
            const rawPurchaseOrder = raw['34'];
            data[code] = {
              description: (raw['1'] as string) ?? '',
              onhand: (raw['13'] as number) ?? 0,
              movement: 0,
              delColMovement: (raw['35'] as number) ?? 0,
              latestCost: (raw['15'] as number) ?? 0,
              barcode: (raw['52'] as string) ?? undefined,
              suppCode: (raw['2'] as string) ?? undefined,
              regularSuppCode: (raw['40'] as string) ?? undefined,
              binL: (raw['53'] as string) ?? undefined,
              packSize: (raw['51'] as number) ?? undefined,
              avCost: (raw['14'] as number) ?? undefined,
              sellPriIncl1: (raw['12'] as number) ?? undefined,
              dep: (raw['3'] as string) ?? undefined,
              subDep: (raw['4'] as string) ?? undefined,
              purchaseOrder: typeof rawPurchaseOrder === 'number' ? rawPurchaseOrder : undefined,
              lastStockTake: parseStockTakeDateToIso(raw[STOCK_FIELD_STOCK_TAKE_DATE]),
              category: raw['5'] != null && raw['5'] !== '' ? String(raw['5']) : undefined,
              reportItemCode: raw['37'] != null ? String(raw['37']) : '',
              reportItemFactor: raw['38'] != null && raw['38'] !== '' ? Number(raw['38']) : undefined,
              noDecimal: raw[sItemKeyToInt.noDecimal],
            };
          } else {
            missingCodes.push(chunk[idx]);
          }
        });
      }),
    );
    return { data, missingCodes };
  }

  /**
   * Store-wide count of items with no valid last stock take date.
   */
  async getNeverCountedStockCount(storeId: string): Promise<{ missingCount: number; totalStockCount: number }> {
    const stockPath = createSharedPaths().setStoreId(storeId).stock();
    const colRef = collection(this.firestore, stockPath);
    const validDateFloor = Timestamp.fromMillis(STOCK_TAKE_NEVER_COUNTED_CUTOFF_MS);
    const totalSnap = await getCountFromServer(query(colRef));
    const totalStockCount = totalSnap.data().count;
    const dateQueryable = await this.resolveStockTakeDateQueryable(storeId, colRef, validDateFloor);
    if (!dateQueryable) {
      return { missingCount: 0, totalStockCount };
    }
    const validDateSnap = await getCountFromServer(
      query(colRef, where(STOCK_FIELD_STOCK_TAKE_DATE, '>=', validDateFloor)),
    );
    return {
      missingCount: totalStockCount - validDateSnap.data().count,
      totalStockCount,
    };
  }

  /**
   * Store-wide dashboard metrics: 6 stale-count bands, never-counted count, total stock value,
   * and stock value counted in the coverage window (last 30 days).
   */
  async getStoreWideStockDashboardMetrics(storeId: string): Promise<IStoreWideStockDashboardMetrics> {
    const stockPath = createSharedPaths().setStoreId(storeId).stock();
    const colRef = collection(this.firestore, stockPath);
    const neverCountedFloor = Timestamp.fromMillis(resolveStockTakeDashboardValidDateFloorMs());
    const presentDateFloor = Timestamp.fromMillis(1);
    const coverageCutoff = new Date();
    coverageCutoff.setDate(coverageCutoff.getDate() - STOCK_TAKE_DASHBOARD_COVERAGE_DAYS);

    const coverageCutoffMs = coverageCutoff.getTime();
    const [totalSnap, totalStockValue, lowStockValueMetrics, reportToItems] = await Promise.all([
      getCountFromServer(query(colRef)),
      this.sumStockValue(colRef),
      this.getLowStockValueMetrics(colRef),
      this.loadReportToItemDateAdjustments(colRef, neverCountedFloor.toMillis(), coverageCutoffMs),
    ]);
    const totalStockCount = totalSnap.data().count;
    const dateQueryable = await this.resolveStockTakeDateQueryable(storeId, colRef, neverCountedFloor);

    if (!dateQueryable) {
      return {
        absentDateFieldCount: 0,
        dateMetricsAvailable: false,
        missingCount: 0,
        neverCountedStockValue: 0,
        lowStockValueItemCount: lowStockValueMetrics.itemCount,
        negativeStockValueTotal: lowStockValueMetrics.negativeValueTotal,
        reportToItemCount: reportToItems.itemCount,
        reportToStockValue: reportToItems.totalStockValue,
        totalStockCount,
        totalStockValue,
        uncountedBands: this.buildEmptyUncountedBands(),
        bandsAvailable: false,
        countedItemsInCoveragePeriod: 0,
        valueCountedInCoveragePeriod: 0,
      };
    }

    const [neverCountedMetrics, presentDateSnap] = await Promise.all([
      this.getNeverCountedMetrics(colRef, neverCountedFloor),
      getCountFromServer(query(colRef, where(STOCK_FIELD_STOCK_TAKE_DATE, '>=', presentDateFloor))),
    ]);
    const { count: neverCountedCount, stockValue: neverCountedStockValue } = neverCountedMetrics;
    const absentDateFieldCount = Math.max(0, totalStockCount - presentDateSnap.data().count);
    const { uncountedBands, bandsAvailable } = await this.buildUncountedBands(
      colRef,
      neverCountedFloor,
      neverCountedCount,
    );
    const valueCountedInCoveragePeriod = await this.sumStockValueCountedSince(storeId, colRef, coverageCutoff);
    const countableNeverCountedCount = Math.max(0, neverCountedCount - reportToItems.neverCountedCount);
    const countableNeverCountedValue = Math.max(0, neverCountedStockValue - reportToItems.neverCountedStockValue);

    return {
      absentDateFieldCount,
      dateMetricsAvailable: true,
      missingCount: countableNeverCountedCount,
      neverCountedStockValue: countableNeverCountedValue,
      lowStockValueItemCount: lowStockValueMetrics.itemCount,
      negativeStockValueTotal: lowStockValueMetrics.negativeValueTotal,
      reportToItemCount: reportToItems.itemCount,
      reportToStockValue: reportToItems.totalStockValue,
      totalStockCount,
      totalStockValue,
      uncountedBands: subtractReportToItemsFromUncountedBands(uncountedBands, reportToItems, bandsAvailable),
      bandsAvailable,
      countedItemsInCoveragePeriod: 0,
      valueCountedInCoveragePeriod: Math.max(
        0,
        valueCountedInCoveragePeriod - reportToItems.valueCountedInCoveragePeriod,
      ),
    };
  }

  /**
   * Items with a non-blank report-item code (field 37). Their dates are removed from dashboard totals.
   */
  private async loadReportToItemDateAdjustments(
    colRef: CollectionReference,
    neverCountedFloorMs: number,
    coverageCutoffMs: number,
  ): Promise<IReportToItemMetricAdjustment> {
    const bandCutoffMs = STOCK_TAKE_DASHBOARD_UNCOUNTED_BAND_DEFS.map((band) => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - band.days);
      return cutoff.getTime();
    });
    const adjustment = createEmptyReportToItemMetricAdjustment(STOCK_TAKE_DASHBOARD_UNCOUNTED_BAND_DEFS.length);
    try {
      const snap = await getDocs(query(colRef, where(sItemKeyToInt.reportItemCode, '>', '')));
      snap.docs.forEach((docSnap) => {
        const raw = docSnap.data();
        const reportItemCode = raw[sItemKeyToInt.reportItemCode] as string | number | null | undefined;
        if (!hasStockTakeReportItemCode(reportItemCode)) {
          return;
        }
        applyReportToItemToMetricAdjustment(
          adjustment,
          raw[STOCK_FIELD_STOCK_TAKE_DATE],
          Number(raw[STOCK_FIELD_STOCK_VALUE] ?? 0),
          neverCountedFloorMs,
          coverageCutoffMs,
          bandCutoffMs,
        );
      });
      return adjustment;
    } catch {
      return adjustment;
    }
  }

  private async buildUncountedBands(
    colRef: CollectionReference,
    validDateFloor: Timestamp,
    neverCountedCount: number,
  ): Promise<{ uncountedBands: IUncountedBand[]; bandsAvailable: boolean }> {
    try {
      const bandStaleSnaps = await Promise.all(
        STOCK_TAKE_DASHBOARD_UNCOUNTED_BAND_DEFS.map((band) => {
          const cutoff = new Date();
          cutoff.setDate(cutoff.getDate() - band.days);
          return getCountFromServer(
            query(
              colRef,
              where(STOCK_FIELD_STOCK_TAKE_DATE, '>=', validDateFloor),
              where(STOCK_FIELD_STOCK_TAKE_DATE, '<', Timestamp.fromDate(cutoff)),
            ),
          );
        }),
      );
      const uncountedBands: IUncountedBand[] = STOCK_TAKE_DASHBOARD_UNCOUNTED_BAND_DEFS.map((band, index) => ({
        label: band.label,
        count: neverCountedCount + bandStaleSnaps[index].data().count,
        tone: band.tone,
      }));
      uncountedBands.push({
        label: 'Never',
        count: neverCountedCount,
        tone: 'neutral',
      });
      return { uncountedBands, bandsAvailable: true };
    } catch (error: unknown) {
      if (!this.isFirestoreStockTakeDateQueryUnsupported(error)) {
        throw error;
      }
      const uncountedBands = this.buildEmptyUncountedBands();
      const neverBand = uncountedBands.find((band) => band.label === 'Never');
      if (neverBand) {
        neverBand.count = neverCountedCount;
      }
      return { uncountedBands, bandsAvailable: false };
    }
  }

  private buildEmptyUncountedBands(): IUncountedBand[] {
    const bands: IUncountedBand[] = STOCK_TAKE_DASHBOARD_UNCOUNTED_BAND_DEFS.map((band) => ({
      label: band.label,
      count: 0,
      tone: band.tone,
    }));
    bands.push({
      label: 'Never',
      count: 0,
      tone: 'neutral',
    });
    return bands;
  }

  private async sumStockValue(colRef: CollectionReference): Promise<number> {
    try {
      const totalValueSnap = await getAggregateFromServer(query(colRef), {
        totalStockValue: sum(STOCK_FIELD_STOCK_VALUE),
      });
      return totalValueSnap.data().totalStockValue ?? 0;
    } catch {
      return 0;
    }
  }

  /**
   * Count items with `_stock_value` of -R2,000 or more negative, and sum those values.
   */
  private async getLowStockValueMetrics(
    colRef: CollectionReference,
  ): Promise<{ itemCount: number; negativeValueTotal: number }> {
    const severeNegativeFilter = where(
      STOCK_FIELD_STOCK_VALUE,
      '<=',
      -STOCK_TAKE_DASHBOARD_LOW_STOCK_VALUE_ZAR,
    );
    const [itemCount, negativeValueTotal] = await Promise.all([
      this.countStockForValueFilter(colRef, severeNegativeFilter),
      this.sumStockValueForValueFilter(colRef, severeNegativeFilter),
    ]);
    return { itemCount, negativeValueTotal };
  }

  private async countStockForValueFilter(
    colRef: CollectionReference,
    valueFilter: ReturnType<typeof where>,
  ): Promise<number> {
    try {
      const snap = await getCountFromServer(query(colRef, valueFilter));
      return snap.data().count;
    } catch {
      return 0;
    }
  }

  private async sumStockValueForValueFilter(
    colRef: CollectionReference,
    valueFilter: ReturnType<typeof where>,
  ): Promise<number> {
    try {
      const valueSnap = await getAggregateFromServer(query(colRef, valueFilter), {
        stockValue: sum(STOCK_FIELD_STOCK_VALUE),
      });
      return valueSnap.data().stockValue ?? 0;
    } catch {
      return 0;
    }
  }

  /**
   * Never-counted = no `_stock_take_date` OR date before the 10-year floor.
   * Two Firestore queries (null + before floor) — a single `< floor` query omits docs with no date field.
   */
  private async getNeverCountedMetrics(
    colRef: CollectionReference,
    validDateFloor: Timestamp,
  ): Promise<{ count: number; stockValue: number }> {
    const missingDateFilter = where(STOCK_FIELD_STOCK_TAKE_DATE, '==', null);
    const belowFloorFilter = where(STOCK_FIELD_STOCK_TAKE_DATE, '<', validDateFloor);

    const [missingCount, belowFloorCount, missingValue, belowFloorValue] = await Promise.all([
      this.countStockForDateFilter(colRef, missingDateFilter),
      this.countStockForDateFilter(colRef, belowFloorFilter),
      this.sumStockValueForDateFilter(colRef, missingDateFilter),
      this.sumStockValueForDateFilter(colRef, belowFloorFilter),
    ]);

    return {
      count: missingCount + belowFloorCount,
      stockValue: missingValue + belowFloorValue,
    };
  }

  private async countStockForDateFilter(
    colRef: CollectionReference,
    dateFilter: ReturnType<typeof where>,
  ): Promise<number> {
    try {
      const snap = await getCountFromServer(query(colRef, dateFilter));
      return snap.data().count;
    } catch (error: unknown) {
      if (this.isFirestoreStockTakeDateQueryUnsupported(error)) {
        return 0;
      }
      throw error;
    }
  }

  private async sumStockValueForDateFilter(
    colRef: CollectionReference,
    dateFilter: ReturnType<typeof where>,
  ): Promise<number> {
    try {
      const valueSnap = await getAggregateFromServer(query(colRef, dateFilter), {
        stockValue: sum(STOCK_FIELD_STOCK_VALUE),
      });
      return valueSnap.data().stockValue ?? 0;
    } catch (error: unknown) {
      if (this.isFirestoreStockTakeDateQueryUnsupported(error)) {
        return 0;
      }
      throw error;
    }
  }

  private async sumStockValueCountedSince(
    storeId: string,
    colRef: CollectionReference,
    countedSince: Date,
  ): Promise<number> {
    const cached = this.stockTakeCoverageValueAggregateQueryableByStoreId.get(storeId);
    if (cached === false) {
      return 0;
    }
    try {
      const coverageValueSnap = await getAggregateFromServer(
        query(colRef, where(STOCK_FIELD_STOCK_TAKE_DATE, '>=', Timestamp.fromDate(countedSince))),
        {
          countedStockValue: sum(STOCK_FIELD_STOCK_VALUE),
        },
      );
      this.stockTakeCoverageValueAggregateQueryableByStoreId.set(storeId, true);
      return coverageValueSnap.data().countedStockValue ?? 0;
    } catch (error: unknown) {
      if (this.isFirestoreStockTakeDateQueryUnsupported(error)) {
        this.stockTakeCoverageValueAggregateQueryableByStoreId.set(storeId, false);
        return 0;
      }
      throw error;
    }
  }

  private async resolveStockTakeDateQueryable(
    storeId: string,
    colRef: CollectionReference,
    validDateFloor: Timestamp,
  ): Promise<boolean> {
    const cached = this.stockTakeDateQueryableByStoreId.get(storeId);
    if (cached !== undefined) {
      return cached;
    }
    try {
      await getCountFromServer(query(colRef, where(STOCK_FIELD_STOCK_TAKE_DATE, '>=', validDateFloor)));
      this.stockTakeDateQueryableByStoreId.set(storeId, true);
      return true;
    } catch (error: unknown) {
      if (this.isFirestoreStockTakeDateQueryUnsupported(error)) {
        this.stockTakeDateQueryableByStoreId.set(storeId, false);
        return false;
      }
      throw error;
    }
  }

  private isFirestoreStockTakeDateQueryUnsupported(error: unknown): boolean {
    const code = (error as { code?: string })?.code;
    return code === 'failed-precondition' || code === 'invalid-argument';
  }

  /**
   * Highest stock-value items ordered by precomputed `_stock_value`.
   */
  async getTopStockItemsByValue(
    storeId: string,
    maxItems: number = STOCK_TAKE_DASHBOARD_TOP_ITEMS_LIMIT,
  ): Promise<ITopValueItem[]> {
    const stockPath = createSharedPaths().setStoreId(storeId).stock();
    const colRef = collection(this.firestore, stockPath);
    const items: ITopValueItem[] = [];
    let cursor: QueryDocumentSnapshot | undefined;
    const nowMs = Date.now();
    const pageSize = Math.max(maxItems, 50);
    while (items.length < maxItems) {
      const topQuery = cursor
        ? query(colRef, orderBy(STOCK_FIELD_STOCK_VALUE, 'desc'), startAfter(cursor), limit(pageSize))
        : query(colRef, orderBy(STOCK_FIELD_STOCK_VALUE, 'desc'), limit(pageSize));
      const snap = await getDocs(topQuery);
      if (snap.empty) {
        break;
      }
      snap.docs.forEach((docSnap) => {
        if (items.length >= maxItems) {
          return;
        }
        const reportItemCode = docSnap.data()[sItemKeyToInt.reportItemCode] as string | number | null | undefined;
        if (hasStockTakeReportItemCode(reportItemCode)) {
          return;
        }
        items.push(this.mapStockDocToTopValueItem(docSnap, nowMs));
      });
      cursor = snap.docs[snap.docs.length - 1];
      if (snap.size < pageSize) {
        break;
      }
    }
    return items;
  }

  /**
   * High-value stock codes not counted within the coverage window.
   * Matches the dashboard KPI: top items by stock value, then filter by last count date.
   */
  async getHighRiskStockCodes(
    storeId: string,
    coverageDays: number = STOCK_TAKE_DASHBOARD_COVERAGE_DAYS,
    topValuePoolLimit: number = STOCK_TAKE_DASHBOARD_TOP_ITEMS_LIMIT,
  ): Promise<string[]> {
    const topItems = await this.getTopStockItemsByValue(storeId, topValuePoolLimit);
    return filterStockTakeHighRiskTopValueItems(topItems, coverageDays).map((item) => item.code);
  }

  private mapStockDocToTopValueItem(docSnap: QueryDocumentSnapshot, nowMs: number): ITopValueItem {
    const raw = docSnap.data();
    const rawDate = raw[STOCK_FIELD_STOCK_TAKE_DATE];
    const daysSinceCounted = resolveStockTakeDaysSinceCounted(rawDate, nowMs);
    const atMs = daysSinceCounted != null ? parseStockTakeDateToMillis(rawDate) : null;
    const onhand = Number(raw['13'] ?? 0);
    const cost = Number(raw['15'] ?? raw['14'] ?? 0);
    const stockValue = Number(raw[STOCK_FIELD_STOCK_VALUE] ?? onhand * cost);
    return {
      code: docSnap.id,
      description: (raw['1'] as string) ?? '',
      category: (raw['3'] as string) ?? '—',
      stockOnHand: onhand,
      costPrice: cost,
      totalValue: stockValue,
      lastCountedAt: atMs != null ? Timestamp.fromMillis(atMs) : null,
      daysSinceCounted,
      hasSchedule: false,
    };
  }

  /**
   * Load final figures for a completed stock take (one-shot).
   */
  async getFinalFiguresOnce(storeId: string, stockTakeId: string): Promise<IStockTakeFinalFigure[]> {
    return this.finalisedChunkService.loadFinalFigures(stockTakePath(storeId, stockTakeId));
  }

  /**
   * Update the quantity counted for a single existing stock take item.
   * Increments timesCounted and sets status to COUNTED.
   * If it is the first time this item is being counted (isFirstCount), also increments countedCount on the parent.
   */
  async updateStockTakeItemCount(
    storeId: string,
    stockTakeId: string,
    itemId: string,
    qtyCounted: number,
    previousQtyCounted: number,
    isFirstCount: boolean,
    userEmail?: string,
    binLocation?: string,
    overrideCountHistory: boolean = false,
  ): Promise<boolean> {
    const takePath = stockTakePath(storeId, stockTakeId);
    const items = await this.itemsChunkService.loadStockTakeItems(takePath);
    const existing = items.find((i) => i.id === itemId || i.code === itemId);
    const timesCounted = overrideCountHistory ? 1 : (existing?.timesCounted ?? 0) + 1;
    const patch: Partial<IStockTakeItemStored> = {
      qtyCounted,
      previousQtyCounted: overrideCountHistory ? 0 : previousQtyCounted,
      timesCounted,
      ...(userEmail ? { countedByEmail: userEmail } : {}),
      ...(binLocation ? { binLocation } : {}),
    };
    if (isFirstCount) {
      patch.status = StockTakeItemStatus.counted;
      patch.firstCountedAt = Timestamp.now();
    }
    await this.itemsChunkService.updateItemByCode(takePath, itemId, patch);
    if (!isFirstCount) {
      return false;
    }
    const parentRef = doc(this.firestore, takePath);
    const parentUpdate: Record<string, unknown> = { countedCount: increment(1) };
    if (existing?.status === StockTakeItemStatus.recount) {
      parentUpdate['recountCount'] = increment(-1);
    }
    const reverted = await this.includePreparedRevert(
      parentRef,
      parentUpdate,
      existing?.status === StockTakeItemStatus.pending,
    );
    await updateDoc(parentRef, parentUpdate);
    return reverted;
  }

  /**
   * Look up a stock code by its barcode value from the shared stock collection.
   * Stock items store barcode under compressed field key '52'.
   * Returns the decoded stock code (document ID), or null if not found.
   */
  async getStockCodeByBarcode(storeId: string, barcode: string): Promise<string | null> {
    const stockPath = createSharedPaths().setStoreId(storeId).stock();
    const colRef = collection(this.firestore, stockPath);
    const q = query(colRef, where('52', '==', barcode));
    const snap = await getDocs(q);
    if (!snap.empty) {
      return toRawStockCode(snap.docs[0].id);
    }
    return null;
  }

  /**
   * Resolve a scanned or manually entered barcode/stock code to a stock code.
   * Mirrors the barcode scanner lookup: primary barcode, stock code, and multiple barcodes.
   */
  async resolveStockCodeFromBarcodeOrCode(storeId: string, value: string): Promise<string | null> {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const byBarcode = await this.getStockCodeByBarcode(storeId, trimmed);
    if (byBarcode) {
      return byBarcode;
    }

    const stockPath = createSharedPaths().setStoreId(storeId).stock();
    const directSnap = await getDoc(doc(this.firestore, `${stockPath}/${toStockDocId(trimmed)}`));
    if (directSnap.exists()) {
      return toRawStockCode(directSnap.id);
    }

    const colRef = collection(this.firestore, stockPath);
    const byCodeSnap = await getDocs(query(colRef, where('0', '==', toRawStockCode(trimmed))));
    if (!byCodeSnap.empty) {
      return toRawStockCode(byCodeSnap.docs[0].id);
    }

    const byMultipleBarcodeSnap = await getDocs(
      query(colRef, where('multipleBarcodes', 'array-contains', trimmed)),
    );
    if (!byMultipleBarcodeSnap.empty) {
      return toRawStockCode(byMultipleBarcodeSnap.docs[0].id);
    }

    return null;
  }

  /**
   * Add a single new item (UNIQUE status) directly with a quantity.
   * Used when a scanned barcode is not in the original stock take list.
   */
  async addSingleStockTakeItem(
    storeId: string,
    stockTakeId: string,
    code: string,
    qtyCounted: number,
    userEmail?: string,
    binLocation?: string,
  ): Promise<void> {
    const takePath = stockTakePath(storeId, stockTakeId);
    const now = Timestamp.now();
    const newItem: Omit<IStockTakeItem, 'id'> = {
      code,
      qtyCounted,
      previousQtyCounted: 0,
      timesCounted: 1,
      status: StockTakeItemStatus.unique,
      addedAt: now,
      firstCountedAt: now,
      ...(userEmail ? { countedByEmail: userEmail } : {}),
      ...(binLocation ? { binLocation } : {}),
    };
    await this.itemsChunkService.batchWriteItems(takePath, [newItem]);
    const parentRef = doc(this.firestore, takePath);
    await updateDoc(parentRef, {
      itemCount: increment(1),
      countedCount: increment(1),
    });
  }

  /**
   * Clear counts on a list item and return it to PENDING (To Count tab).
   * Resolves the current line from Firestore so countedCount stays accurate after optimistic UI updates.
   */
  async resetStockTakeItemToPending(
    storeId: string,
    stockTakeId: string,
    itemId: string,
  ): Promise<void> {
    const takePath = stockTakePath(storeId, stockTakeId);
    const storedItems = await this.itemsChunkService.loadStockTakeItems(takePath);
    const stored = storedItems.find((line) => line.id === itemId);
    if (!stored) {
      throw new Error(`Stock take item "${itemId}" could not be found`);
    }
    const wasCounted =
      stored.status === StockTakeItemStatus.counted ||
      stored.status === StockTakeItemStatus.unique;
    const wasRecount = stored.status === StockTakeItemStatus.recount;
    await this.itemsChunkService.updateItemByCode(takePath, stored.code, {
      qtyCounted: 0,
      previousQtyCounted: 0,
      timesCounted: 0,
      status: StockTakeItemStatus.pending,
    });
    const parentRef = doc(this.firestore, takePath);
    const parentUpdate: Record<string, unknown> = {};
    if (wasCounted) {
      parentUpdate['countedCount'] = increment(-1);
    } else if (wasRecount) {
      parentUpdate['recountCount'] = increment(-1);
    }
    if (Object.keys(parentUpdate).length > 0) {
      await updateDoc(parentRef, parentUpdate);
    }
  }

  /**
   * Batch-delete stock take items by their stock codes.
   * Resolves item status from Firestore (not NgRx) so deletes still work after optimistic UI updates.
   * Also decrements itemCount (and countedCount for non-pending items) on the parent document.
   */
  async removeStockTakeItemsByCodes(
    storeId: string,
    stockTakeId: string,
    codesToRemove: string[],
  ): Promise<boolean> {
    const takePath = stockTakePath(storeId, stockTakeId);
    if (codesToRemove.length === 0) {
      return false;
    }
    const removedItems = await this.itemsChunkService.removeItemsByCodes(takePath, codesToRemove);
    if (removedItems.length === 0) {
      throw new Error(
        'Could not remove items from the stock take. This may be a sync issue — please try again or contact Gallix support.',
      );
    }
    const countedRemoved = removedItems.filter(
      (i) => i.status === StockTakeItemStatus.counted || i.status === StockTakeItemStatus.unique,
    ).length;
    const recountRemoved = removedItems.filter((i) => i.status === StockTakeItemStatus.recount).length;
    const pendingRemoved = removedItems.some((i) => i.status === StockTakeItemStatus.pending);
    const parentRef = doc(this.firestore, takePath);
    const parentUpdate: Record<string, unknown> = { itemCount: increment(-removedItems.length) };
    if (countedRemoved > 0) {
      parentUpdate['countedCount'] = increment(-countedRemoved);
    }
    if (recountRemoved > 0) {
      parentUpdate['recountCount'] = increment(-recountRemoved);
    }
    const reverted = await this.includePreparedRevert(parentRef, parentUpdate, pendingRemoved);
    await updateDoc(parentRef, parentUpdate);
    return reverted;
  }

  /**
   * Total, UNIQUE, and non-UNIQUE (base/list) item counts for capacity checks.
   */
  async getStockTakeItemCapacity(storeId: string, stockTakeId: string): Promise<IStockTakeItemCapacity> {
    return this.itemsChunkService.getItemCapacity(stockTakePath(storeId, stockTakeId));
  }

  /**
   * Get all items for a stock take (real-time listener, chunked layout).
   */
  getStockTakeItems(storeId: string, stockTakeId: string): Observable<IStockTakeItem[]> {
    return this.itemsChunkService.watchStockTakeItems(stockTakePath(storeId, stockTakeId));
  }

  /**
   * Finalise a stock take:
   *  1. Writes final figures to chunked finalised storage
   *  2. Sets status SUBMITTED and finalised metadata on the parent document
   */
  async finaliseStockTake(
    storeId: string,
    stockTakeId: string,
    finalFigures: Omit<IStockTakeFinalFigure, 'id'>[],
    processedByEmail: string,
  ): Promise<number> {
    const takePath = stockTakePath(storeId, stockTakeId);
    try {
      const finalisedItemCount = await this.finalisedChunkService.writeFinalFigures(takePath, finalFigures);
      const stockTakeDocRef = doc(this.firestore, takePath);
      await updateDoc(stockTakeDocRef, {
        status: StockTakeStatus.submitted,
        finalisedStorageSchema: STOCK_TAKE_FINALISED_STORAGE_SCHEMA_CURRENT,
        finalisedItemCount,
        processedByEmail,
      });
      return finalisedItemCount;
    } catch (error) {
      console.error('[StockTake] finaliseStockTake FAILED. Path attempted:', takePath, error);
      throw error;
    }
  }

  /**
   * Build STOCK_TAKE from_app payload data for the store service.
   */
  buildStockTakeMessageData(stockTakeId: string, finalisedItemCount: number): IStockTakeFromAppMessageData {
    return {
      stockTakeId,
      finalisedStorageSchema: STOCK_TAKE_FINALISED_STORAGE_SCHEMA_CURRENT,
      finalisedItemCount,
    };
  }

  /**
   * Create an API event log and send a STOCK_UPDATE from_app message for per-item bin location changes.
   * Uses the same pattern as the stock manager stockUpdate() function so the server-side
   * service processes bin location changes identically to stock manager updates.
   */
  async sendBinLocationStockUpdates(
    storeId: string,
    itemBinLocations: { code: string; binLocation: string }[],
    userId: string,
  ): Promise<void> {
    if (itemBinLocations.length === 0) return;
    const msgColPath = path_operational_stores_data_storeId_messages_from_app(storeId);
    const apiEventsPath = `operational/stores_data/${storeId}/data/api_events/`;
    const data: Record<string, { o: Record<string, string>; n: Record<string, string>; details: Record<string, never> }> = {};
    for (const { code, binLocation } of itemBinLocations) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      data[code] = { o: { '53': '' }, n: { '53': binLocation }, details: {} };
    }
    await runTransaction(this.firestore, async (trans) => {
      const logRef = doc(collection(this.firestore, apiEventsPath));
      const msgRef = doc(collection(this.firestore, msgColPath));
      trans.set(logRef, {
        creationDate: createOperationalServerTimestamp(),
        data,
        type: 'STOCK_UPDATE',
        userId,
        editedItems: itemBinLocations.map((i) => i.code),
      });
      trans.set(msgRef, {
        type: 'STOCK_UPDATE',
        sender: userId,
        timestamp: createOperationalServerTimestamp(),
        payload: { data: { logID: logRef.id } },
      });
    });
  }

  /**
   * Send a STOCK_TAKE message to the operational messages collection after finalisation.
   * The backend processes this message to apply the stock take adjustments.
   */
  async sendStockTakeMessage(
    storeId: string,
    stockTakeId: string,
    userEmail: string,
    messageData?: IStockTakeFromAppMessageData,
  ): Promise<void> {
    const msgColPath = path_operational_stores_data_storeId_messages_from_app(storeId);
    const payloadData =
      messageData ?? this.buildStockTakeMessageData(stockTakeId, 0);
    try {
      const msgRef = doc(collection(this.firestore, msgColPath));
      await setDoc(msgRef, {
        type: STOCK_TAKE_MESSAGE_TYPE,
        sender: userEmail,
        timestamp: createOperationalServerTimestamp(),
        payload: {
          data: payloadData,
        },
      });
    } catch (error) {
      console.error('[StockTake] sendStockTakeMessage FAILED. Path attempted:', msgColPath, error);
      throw error;
    }
  }

  /**
   * Re-queue backend STOCK_TAKE processing after a failure.
   * Sets status to SUBMITTED (never PREPARED) and writes the from_app message.
   */
  async resendStockTakeProcessingMessage(
    storeId: string,
    stockTakeId: string,
    userEmail: string,
  ): Promise<void> {
    const msgColPath = path_operational_stores_data_storeId_messages_from_app(storeId);
    const stockTakeDocRef = doc(this.firestore, stockTakePath(storeId, stockTakeId));
    const msgRef = doc(collection(this.firestore, msgColPath));
    try {
      const takeSnap = await getDoc(stockTakeDocRef);
      const take = takeSnap.data() as IStockTake | undefined;
      const payloadData = this.buildStockTakeMessageData(
        stockTakeId,
        take?.finalisedItemCount ?? 0,
      );
      const batch = writeBatch(this.firestore);
      batch.update(stockTakeDocRef, {
        status: StockTakeStatus.submitted,
        processedByEmail: userEmail,
      });
      batch.set(msgRef, {
        type: STOCK_TAKE_MESSAGE_TYPE,
        sender: userEmail,
        timestamp: createOperationalServerTimestamp(),
        payload: {
          data: payloadData,
        },
      });
      await batch.commit();
    } catch (error) {
      console.error(
        '[StockTake] resendStockTakeProcessingMessage FAILED. Paths attempted:',
        stockTakePath(storeId, stockTakeId),
        msgColPath,
        error,
      );
      throw error;
    }
  }

  /**
   * Replace stored final figures after removing rejected items, without changing status.
   */
  async replaceFinalFigures(
    storeId: string,
    stockTakeId: string,
    figures: IStockTakeFinalFigure[],
  ): Promise<number> {
    const takePath = stockTakePath(storeId, stockTakeId);
    const payload: Omit<IStockTakeFinalFigure, 'id'>[] = figures.map((figure) => {
      const { id: _id, ...rest } = figure;
      return rest;
    });
    const finalisedItemCount = await this.finalisedChunkService.writeFinalFigures(takePath, payload);
    const stockTakeDocRef = doc(this.firestore, takePath);
    await updateDoc(stockTakeDocRef, { finalisedItemCount });
    return finalisedItemCount;
  }

  /**
   * Get the final figures for a submitted stock take (real-time).
   */
  getFinalFigures(storeId: string, stockTakeId: string): Observable<IStockTakeFinalFigure[]> {
    return this.finalisedChunkService.watchFinalFigures(stockTakePath(storeId, stockTakeId));
  }
}
